import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToggleFavorite, useCheckFavorited } from "@/hooks/useFavorites";
import { cn } from "@/lib/utils";
import { getScoreColor } from "@/lib/scoreUtils";
import { RemixButton } from "@/components/feed/RemixButton";
import { RemixAvatarStack } from "@/components/feed/RemixAvatarStack";
import { PublishButton } from "@/components/shared/PublishButton";
import { PromptActionsMenu } from "@/components/shared/PromptActionsMenu";
import type { LibraryPrompt } from "@/hooks/useLibraryData";

const CATEGORY_COLORS: Record<string, string> = {
  'Planning': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Strategy': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'Research': 'bg-green-500/20 text-green-300 border-green-500/30',
  'Drafting': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  'Analysis': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  'Creative': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
  'Brainstorming': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  'Summarizing': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
  'Unknown': 'bg-muted text-muted-foreground border-border',
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getPromptSummary(prompt: LibraryPrompt): string {
  if (prompt.evaluation_markdown) {
    try {
      const parsed = JSON.parse(prompt.evaluation_markdown);
      if (parsed.prompt_summary) return parsed.prompt_summary;
    } catch {}
  }
  return 'Provides structured guidance for your task';
}

function getPromptTitle(prompt: LibraryPrompt): string | null {
  if (prompt.evaluation_markdown) {
    try {
      const parsed = JSON.parse(prompt.evaluation_markdown);
      if (parsed.prompt_title) return parsed.prompt_title;
    } catch {}
  }
  return null;
}

interface LibraryCardProps {
  prompt: LibraryPrompt;
  onViewDetail?: (prompt: LibraryPrompt) => void;
  onRemix?: (prompt: LibraryPrompt) => void;
  onViewRemixers?: (prompt: LibraryPrompt) => void;
  onHidePrompt?: (prompt: LibraryPrompt) => void;
  onDeletePrompt?: (prompt: LibraryPrompt) => void;
  folderBadge?: string;
  ownerName?: string;
  ownerInitials?: string;
  /** Show publish/unpublish controls (for "My Prompts" tab) */
  showPublishStatus?: boolean;
  /** Submission ID for publish toggle (required when showPublishStatus is true) */
  submissionId?: string;
}

export function LibraryCard({ prompt, onViewDetail, onRemix, onViewRemixers, onHidePrompt, onDeletePrompt, folderBadge, ownerName, ownerInitials, showPublishStatus, submissionId }: LibraryCardProps) {
  const { user } = useAuth();
  const toggleMutation = useToggleFavorite();
  const { data: isFavorited, isLoading: isFavLoading } = useCheckFavorited(user?.id, prompt.score_id);
  
  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }
    toggleMutation.mutate({ userId: user.id, scoreId: prompt.score_id, isFavorited: !!isFavorited });
  };

  const trackAction = async (actionType: 'copy' | 'share_chatgpt') => {
    if (!user) return;
    try {
      await (supabase.from('prompt_actions' as any) as any).insert({
        score_id: prompt.score_id,
        user_id: user.id,
        action_type: actionType,
      });
    } catch (err) {
      console.error('Failed to track action:', err);
    }
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const textToCopy = prompt.best_prompt || prompt.original_content;
    if (textToCopy) {
      await navigator.clipboard.writeText(textToCopy);
      toast.success('Prompt copied to clipboard');
      trackAction('copy');
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackAction('share_chatgpt');
  };

  const chatGptUrl = prompt.best_prompt 
    ? `https://chatgpt.com/?prompt=${encodeURIComponent(prompt.best_prompt)}`
    : '#';

  

  // Determine if we should show the avatar header (for favorites from others)
  const showAvatarHeader = !!ownerName;

  return (
    <div 
      className="border-b border-border/30 p-4 md:p-5 hover:bg-muted/10 transition-colors cursor-pointer"
      onClick={() => onViewDetail?.(prompt)}
    >
      {/* Folder badge (for search results) */}
      {folderBadge && (
        <div className="mb-2">
          <Badge variant="outline" className="text-xs uppercase tracking-wide">
            From {folderBadge}
          </Badge>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex-1 min-w-0">
          {/* Row 1: Timestamp pinned right */}
          <div className="flex justify-end">
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(prompt.created_at)}
            </span>
          </div>

        {/* Row 2: Score + "Prompt Score" label + Category badge */}
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-baseline gap-1.5">
            <span className={`font-bold text-xl ${getScoreColor(prompt.overall_score)}`}>
              {prompt.overall_score}
            </span>
            <span className="text-sm text-muted-foreground/60">/100</span>
            <span className="text-xs text-muted-foreground ml-1">Prompt Score</span>
          </div>
          <Badge 
            variant="outline" 
            className={`text-xs border flex-shrink-0 rounded-md ${CATEGORY_COLORS[prompt.detected_type || 'Unknown'] || CATEGORY_COLORS['Unknown']}`}
          >
            {prompt.detected_type || 'Unknown'}
          </Badge>
        </div>

        {/* Title (bold) + Summary text */}
        <div className="mt-3">
          {getPromptTitle(prompt) && (
            <h3 className="text-[17px] font-semibold text-foreground mb-1">
              {getPromptTitle(prompt)}
            </h3>
          )}
          <p className="text-[15px] text-foreground/80 leading-relaxed">
            {getPromptSummary(prompt)}
          </p>
        </div>


        {/* PublishButton hidden — all prompts auto-publish */}

        {/* Actions - matching FeedCard layout exactly */}
        <div className="flex items-center justify-between mt-3" onClick={(e) => e.stopPropagation()}>
          {/* Left side: Star (with count) + Copy + ChatGPT */}
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className={cn("h-8 px-2 gap-1", isFavLoading && "opacity-50")}
              onClick={handleFavoriteToggle}
              disabled={toggleMutation.isPending || isFavLoading}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star className={cn(
                "h-4 w-4",
                isFavorited 
                  ? "fill-yellow-500 text-yellow-500" 
                  : "text-muted-foreground"
              )} />
              {prompt.favorite_count > 0 && (
                <span className="text-xs text-muted-foreground">{prompt.favorite_count}</span>
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={handleCopy}
              title="Copy prompt"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              asChild
              title="Try in ChatGPT"
              onClick={handleShare}
            >
              <a href={chatGptUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
              {prompt.submission_id && (
                <PromptActionsMenu
                  submissionId={prompt.submission_id}
                  promptUserId={user?.id || ""}
                  isPublic={prompt.is_public}
                  onHide={() => onHidePrompt?.(prompt)}
                  onDelete={() => onDeletePrompt?.(prompt)}
                />
              )}
          </div>
          
            {/* Right side: Remixer avatars + Remix button */}
            <div className="flex items-center gap-2">
              {prompt.remix_count > 0 && prompt.recent_remixers && (
                <RemixAvatarStack
                  remixers={prompt.recent_remixers}
                  totalCount={prompt.remix_count}
                  onClick={() => onViewRemixers?.(prompt)}
                />
              )}
              <RemixButton onClick={() => onRemix?.(prompt as any)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
